import {
  createContext,
  Dispatch,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useReducer,
} from 'react';
import { isFunction } from 'shared/lib';

import { Step } from './stepper.types';

interface StepConfig {
  route: Step;
  component: ReactNode;
}

interface StepperOptions {
  steps: Array<StepConfig>;
}

interface StepperProvider extends PropsWithChildren {
  initialStep?: Step;
}

type Actions =
  | {
      type: 'STEPPER/PUSH_STEP';
      payload: Step;
    }
  | {
      type: 'STEPPER/GO_BACK';
    }
  | {
      type: 'STEPPER/REPLACE';
      payload: Step;
    };

type State = {
  steps: Array<Step>;
};

const reducer = (state: State, action: Actions): State => {
  switch (action.type) {
    case 'STEPPER/PUSH_STEP': {
      return {
        ...state,
        steps: [...state.steps, action.payload],
      };
    }
    case 'STEPPER/GO_BACK': {
      return {
        ...state,
        steps: state.steps.slice(0, state.steps.length - 1),
      };
    }
    case 'STEPPER/REPLACE': {
      return {
        ...state,
        steps: [action.payload],
      };
    }
    default: {
      return state;
    }
  }
};

interface PrivateStepperCtx {
  state: State;
  dispatch: Dispatch<Actions>;
}

interface StepperCtx<Steps extends Step = string> {
  step: Steps;
  next(route: Steps): void;
  goBack(): void;
  replace(route: Steps): void;
}

const Ctx = createContext({ _fallback: true } as unknown as PrivateStepperCtx);

const usePrivateStepper = () => useContext(Ctx);

export const useStepper = <Steps extends string = string>(): StepperCtx<Steps> => {
  const { state, dispatch } = usePrivateStepper();

  const next: StepperCtx['next'] = useCallback(
    (route) => {
      dispatch({ type: 'STEPPER/PUSH_STEP', payload: route });
    },
    [dispatch],
  );

  const goBack: StepperCtx['goBack'] = useCallback(() => {
    dispatch({ type: 'STEPPER/GO_BACK' });
  }, [dispatch]);

  const replace: StepperCtx['replace'] = useCallback(
    (route) => {
      dispatch({ type: 'STEPPER/REPLACE', payload: route });
    },
    [dispatch],
  );

  return {
    next,
    goBack,
    replace,
    step: state.steps[state.steps.length - 1] as Steps,
  };
};

export const createStepper = (options: StepperOptions) => {
  const { steps } = options;
  const defaultStep = steps[0].route;

  const componentMap = options.steps.reduce((acc, el) => {
    acc[el.route] = el;
    return acc;
  }, {} as Record<Step, StepConfig>);

  const Provider = ({ children, initialStep }: StepperProvider) => {
    const firstStep = initialStep && steps.find((s) => s.route === initialStep) ? initialStep : defaultStep;

    const [state, dispatch] = useReducer(reducer, { steps: [firstStep] });

    const value = useMemo(() => ({ state, dispatch }), [state, dispatch]);

    const toRender = state.steps[state.steps.length - 1];
    const { component } = componentMap[toRender];

    return (
      <Ctx.Provider value={value}>
        {component}
        {children}
      </Ctx.Provider>
    );
  };

  return {
    Provider,
  };
};

interface StepperConsumerProps {
  children: ReactNode | ((props: StepperCtx) => ReactNode);
}

export const StepperConsumer = ({ children }: StepperConsumerProps) => {
  const ctx = useStepper();

  return isFunction(children) ? children(ctx) : children;
};
export const createTypedStepper = <T extends string>() => useStepper<T>;
